import { useState } from "react";
import { toast } from "sonner";
import { Bot, Loader2, Sparkles, Handshake } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

function renderLine(line, i) {
  // bold **text** segments
  const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return (
    <p key={i} className="text-sm text-xd-muted leading-relaxed">
      {parts.map((p, j) =>
        p.startsWith("**") && p.endsWith("**")
          ? <span key={j} className="text-xd-gold font-bold">{p.slice(2, -2)}</span>
          : <span key={j}>{p}</span>
      )}
    </p>
  );
}

export function AIDispatchPanel({ loadId, onUseInBid }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!user) { toast.info("Log in as a carrier to use XD Dispatch AI"); nav("/login"); return; }
    if (user.role !== "carrier") { toast.error("XD Dispatch AI is for carriers"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/carrier/ai-dispatch/${loadId}`);
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-xd-paper border border-xd-blue/40 rounded-xl p-6">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-lg bg-xd-blue/20 text-xd-blue"><Bot size={20} /></span>
        <div>
          <h2 className="font-heading text-lg font-bold">XD Dispatch AI</h2>
          <p className="text-xs text-xd-dim">Elite AI analysis: book/bid/pass, numbers & negotiation</p>
        </div>
      </div>

      {!result && (
        <Button data-testid="ai-dispatch-run-btn" onClick={run} disabled={busy}
          className="mt-4 w-full h-11 bg-xd-blue hover:bg-xd-blueH text-white font-bold rounded-xl">
          {busy ? <><Loader2 className="animate-spin mr-2" size={16} /> Analyzing…</> : <><Sparkles size={16} className="mr-2" /> Ask XD Dispatch AI</>}
        </Button>
      )}

      {result && (
        <div data-testid="ai-dispatch-result" className="mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Num label="Rate / Mile" value={`$${result.numbers.rate_per_mile}`} />
            <Num label="Est. Fuel" value={`$${result.numbers.est_fuel}`} />
            <Num label="Est. Op Cost" value={`$${result.numbers.est_operating_cost}`} />
            <Num label="Est. Net" value={`$${result.numbers.est_net}`} accent />
          </div>
          <div className="space-y-2 border-t border-xd-border pt-4">
            {result.analysis.split("\n").filter((l) => l.trim()).map(renderLine)}
          </div>
          {onUseInBid && result.suggested_counter != null && (
            <button
              data-testid="ai-use-in-bid-btn"
              onClick={() => onUseInBid(result.suggested_counter, result.suggested_note)}
              className="mt-4 w-full h-11 rounded-xl bg-xd-gold hover:bg-xd-goldH text-xd-bg font-bold flex items-center justify-center gap-2 transition-colors">
              <Handshake size={16} /> Counter at ${result.suggested_counter?.toLocaleString()} — 1-tap bid
            </button>
          )}
          <Button data-testid="ai-dispatch-rerun-btn" variant="outline" onClick={run} disabled={busy}
            className="mt-2 w-full border-xd-border text-white hover:bg-xd-surface rounded-xl">
            {busy ? <Loader2 className="animate-spin" size={16} /> : "Re-analyze"}
          </Button>
        </div>
      )}
    </div>
  );
}

function Num({ label, value, accent }) {
  return (
    <div className="bg-xd-surface border border-xd-border rounded-lg px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-xd-dim">{label}</p>
      <p className={`font-heading font-bold ${accent ? "text-xd-green" : "text-white"}`}>{value}</p>
    </div>
  );
}
